import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { requestErrorMessage } from '@/helpers/apiClient'

/**
 * Enter a venue (venue-admin scope for that venue) and land on its dashboard,
 * which is the venue's home. Shared by the venues list, the venue's own header
 * and the sidebar picker.
 */
export function useEnterVenue() {
  const { enterVenue } = useAuth()
  const navigate = useNavigate()
  const [enteringId, setEnteringId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const enter = React.useCallback(
    async (venueId: string): Promise<boolean> => {
      setError(null)
      setEnteringId(venueId)
      try {
        await enterVenue(venueId)
        // The venue's own page, not its events: entering a venue is asking to
        // work in it, and what is on comes before the list of what is on. The
        // path is built here rather than read from the session, which the enter
        // call has only just changed.
        navigate(`/venues/${venueId}`, { replace: true })
        return true
      } catch (e) {
        setError(requestErrorMessage(e))
        // Reported rather than thrown, so callers can keep their menu open on a
        // failure instead of dismissing it as though the switch had happened.
        return false
      } finally {
        // Cleared on success too, not just on failure. The picker lives in the
        // sidebar and survives the navigation, so a lingering id left it stuck:
        // a spinner on the venue just entered and every other venue disabled,
        // making the switch a one-time move.
        setEnteringId(null)
      }
    },
    [enterVenue, navigate],
  )

  return {
    enter,
    enteringId,
    isEntering: enteringId !== null,
    error,
  }
}
